import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { McpTokenWithToken } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { CopyIcon } from '@atoms/Icon/CopyIcon';

import {
  CREATE_MCP_REVEAL_COPY_TEST_ID,
  CREATE_MCP_REVEAL_DONE_LABEL,
  CREATE_MCP_REVEAL_DONE_TEST_ID,
  CREATE_MCP_REVEAL_TEST_ID,
  CREATE_MCP_REVEAL_TOKEN_LABEL,
  CREATE_MCP_REVEAL_TOKEN_TEST_ID,
  CREATE_MCP_REVEAL_WARNING
} from './constants';

type TokenCreatedPanelProps = {
  token: McpTokenWithToken;
  onDone: () => void;
};

export const TokenCreatedPanel = ({
  token,
  onDone
}: TokenCreatedPanelProps) => {
  const openNotification = useOpenNotification();

  const onCopySuccess = () =>
    openNotification({
      kind: 'success',
      message: `${CREATE_MCP_REVEAL_TOKEN_LABEL} copied to your clipboard`
    });

  return (
    <div
      data-hj-suppress
      data-testid={CREATE_MCP_REVEAL_TEST_ID}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold leading-[14px] text-content-primary">
          {CREATE_MCP_REVEAL_TOKEN_LABEL}
        </p>
        <div className="flex items-center justify-between gap-2 rounded-lg bg-surface-muted p-2">
          <span
            data-testid={CREATE_MCP_REVEAL_TOKEN_TEST_ID}
            className="flex-1 break-all font-mono text-xs leading-[22px] tracking-[0.36px] text-content-primary"
          >
            {token.token}
          </span>
          <CopyToClipboard text={token.token} onCopy={onCopySuccess}>
            <button
              type="button"
              data-testid={CREATE_MCP_REVEAL_COPY_TEST_ID}
              aria-label={`Copy ${CREATE_MCP_REVEAL_TOKEN_LABEL}`}
              className="cursor-pointer text-content-secondary hover:text-content-primary"
            >
              <CopyIcon
                width={20}
                height={20}
                stroke="auto"
                className="shrink-0"
              />
            </button>
          </CopyToClipboard>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md bg-feedback-warning-surface p-3 text-sm text-content-primary">
        <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-feedback-attention" />
        <span>{CREATE_MCP_REVEAL_WARNING}</span>
      </div>

      <div className="flex justify-end">
        <PrimaryButton
          data-testid={CREATE_MCP_REVEAL_DONE_TEST_ID}
          onClick={onDone}
        >
          {CREATE_MCP_REVEAL_DONE_LABEL}
        </PrimaryButton>
      </div>
    </div>
  );
};
